import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import UpperHeader from "../../components/header/UpperHeader";
import Header from "../../components/header/Header";
import Title from "../../components/title/Title";
import Subtitle from "../../components/subtitle/Subtitle";
import Paragraph from "../../components/paragraph/Paragraph";
import Footer from "../../components/footer/Footer";
import { useScreenSizes } from "../../components/WindowSizeUtils";

const ACCENT = "#F59A23";

type DayPhotos = { [dayKey: string]: string };

type DayDef = {
  key: string;
  label: string;
  date: string;
  published: boolean;
};

type FungiEventData = {
  eventName: string;
  eventDate: string;
  participantCount: number;
  days: DayDef[];
  photos: { [no: string]: DayPhotos };
};

const pad3 = (n: number) => String(n).padStart(3, "0");

/* ------------------------------------------------------------------ */
/* 拡大表示用のモーダル                                                 */
/* ------------------------------------------------------------------ */
type LightboxProps = {
  src: string;
  caption: string;
  onClose: () => void;
};

const Lightbox: React.FC<LightboxProps> = ({ src, caption, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.85)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        boxSizing: "border-box",
        cursor: "zoom-out",
      }}
    >
      <img
        src={`${process.env.PUBLIC_URL}/${src}`}
        alt={caption}
        style={{
          maxWidth: "98%",
          maxHeight: "82%",
          objectFit: "contain",
          borderRadius: "10px",
          backgroundColor: "white",
        }}
      />
      <p
        style={{
          color: "white",
          fontFamily: "Noto Sans JP",
          fontWeight: 600,
          marginTop: "16px",
          textAlign: "center",
        }}
      >
        {caption}
        <br />
        <span style={{ fontSize: "10pt", fontWeight: 400 }}>
          画面をタップするともどります
        </span>
      </p>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* 1日分のシャーレ写真                                                  */
/* ------------------------------------------------------------------ */
type PhotoCellProps = {
  src?: string;
  dayLabel: string;
  dateLabel: string;
  published: boolean;
  no: number;
  onZoom: (src: string, caption: string) => void;
};

const PhotoCell: React.FC<PhotoCellProps> = ({
  src,
  dayLabel,
  dateLabel,
  published,
  no,
  onZoom,
}) => {
  const { isMobileScreen } = useScreenSizes();
  const ready = published && !!src;

  return (
    <div style={{ flex: "1 1 300px", minWidth: 0 }}>
      <p
        style={{
          fontFamily: "Noto Sans JP",
          fontWeight: 600,
          fontSize: isMobileScreen ? "12pt" : "15pt",
          margin: "0 0 6px 0",
          textAlign: "center",
        }}
      >
        {dayLabel}
        {dateLabel && (
          <span
            style={{
              fontSize: isMobileScreen ? "8pt" : "10pt",
              fontWeight: 400,
              marginLeft: "6px",
              color: "#666",
            }}
          >
            （{dateLabel}）
          </span>
        )}
      </p>
      {ready ? (
        <img
          src={`${process.env.PUBLIC_URL}/${src}`}
          alt={`${no}番のシャーレの${dayLabel}のようす`}
          onClick={() =>
            onZoom(src as string, `${no}番のシャーレ ／ ${dayLabel}`)
          }
          style={{
            width: "100%",
            aspectRatio: "4 / 3",
            objectFit: "cover",
            borderRadius: "12px",
            border: "2px solid #E0E0E0",
            cursor: "zoom-in",
            display: "block",
            backgroundColor: "#000",
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            aspectRatio: "4 / 3",
            borderRadius: "12px",
            border: "2px dashed #C0C0C0",
            backgroundColor: "#FAFAFA",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            fontFamily: "Noto Sans JP",
            textAlign: "center",
            padding: "10px",
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              fontSize: isMobileScreen ? "12pt" : "15pt",
              fontWeight: 600,
              color: "#666",
            }}
          >
            どんなふうに
            <br />
            生えてくるかな？
          </span>
          <span
            style={{
              fontSize: isMobileScreen ? "9pt" : "10pt",
              color: "#999",
              marginTop: "8px",
            }}
          >
            写真がとれたらここにのるよ
          </span>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* ページ本体                                                           */
/* ------------------------------------------------------------------ */
const FungiWorld: React.FC = () => {
  const { isMobileScreen, isSmallScreen } = useScreenSizes();
  const [data, setData] = useState<FungiEventData | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [zoom, setZoom] = useState<{ src: string; caption: string } | null>(
    null
  );

  const selected = useMemo(() => {
    const raw = searchParams.get("no");
    if (!raw) return null;
    const n = parseInt(raw.replace(/[^0-9]/g, ""), 10);
    if (!n || n < 1) return null;
    return n;
  }, [searchParams]);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/fungiEventData.json`)
      .then((res) => res.json())
      .then((json: FungiEventData) => setData(json))
      .catch(() => setData(null));
  }, []);

  useEffect(() => {
    if (selected) setInputValue(String(selected));
  }, [selected]);

  const handleSearch = (value: string) => {
    const n = parseInt(value.replace(/[^0-9]/g, ""), 10);
    const max = data?.participantCount ?? 79;
    if (!n || n < 1 || n > max) {
      setError(`1 から ${max} までの番号をいれてね`);
      return;
    }
    setError("");
    setSearchParams({ no: String(n) });
    setShowAll(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const photos = selected && data ? data.photos[pad3(selected)] : undefined;

  const dayNames = data?.days.length
    ? data.days.map((d) => d.label).join("と")
    : "1日目とあとの日";

  const inputBoxStyle: React.CSSProperties = {
    fontFamily: "Noto Sans JP",
    fontWeight: 600,
    fontSize: isMobileScreen ? "20pt" : "28pt",
    width: isMobileScreen ? "130px" : "170px",
    textAlign: "center",
    padding: "6px 10px",
    border: `3px solid ${ACCENT}`,
    borderRadius: "12px",
    outline: "none",
  };

  return (
    <div>
      <UpperHeader />
      <Header pageTitle="活動実績" />
      <Title text="ふしぎな菌の世界を学ぼう" />

      <Paragraph
        text={
          "イベントでは、納豆菌（なっとうきん）と麹菌（こうじきん）を「絵の具」にして、シャーレの上にお絵かきをしました。かいた絵はすぐには見えませんが、菌がふえてくると、だんだん絵がうかび上がってきます。\\r\\nこのページでは、みんながかいた2枚のシャーレが育っていくようすを写真でしょうかいします。ワークシートに書いてある自分の番号をいれてみてね！"
        }
      />

      {/* ------------------ 番号検索 ------------------ */}
      <div
        style={{
          margin: isMobileScreen ? "20px 5%" : "30px 10%",
          padding: isMobileScreen ? "20px 14px" : "30px",
          border: `3px solid ${ACCENT}`,
          borderRadius: "24px",
          backgroundColor: "#FFF9F0",
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontFamily: "Noto Sans JP",
            fontWeight: 600,
            fontSize: isMobileScreen ? "13pt" : isSmallScreen ? "15pt" : "18pt",
            margin: "0 0 14px 0",
          }}
        >
          自分の番号をいれてね
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch(inputValue);
          }}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <input
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            min={1}
            max={data?.participantCount ?? 79}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="7"
            aria-label="参加者番号"
            style={inputBoxStyle}
          />
          <span
            style={{
              fontFamily: "Noto Sans JP",
              fontWeight: 600,
              fontSize: isMobileScreen ? "14pt" : "20pt",
            }}
          >
            番
          </span>
          <button
            type="submit"
            style={{
              fontFamily: "Noto Sans JP",
              fontWeight: 600,
              fontSize: isMobileScreen ? "13pt" : "18pt",
              color: "white",
              backgroundColor: ACCENT,
              border: "none",
              borderRadius: "12px",
              padding: isMobileScreen ? "10px 22px" : "12px 34px",
              cursor: "pointer",
            }}
          >
            さがす
          </button>
        </form>
        {error && (
          <p
            style={{
              fontFamily: "Noto Sans JP",
              color: "#D20000",
              fontSize: isMobileScreen ? "10pt" : "12pt",
              margin: "12px 0 0 0",
            }}
          >
            {error}
          </p>
        )}
        <p
          style={{
            fontFamily: "Noto Sans JP",
            fontSize: isMobileScreen ? "9pt" : "11pt",
            color: "#666",
            margin: "14px 0 0 0",
          }}
        >
          番号がわからないときは、下の「みんなのシャーレ」からさがしてね
        </p>
      </div>

      {/* ------------------ 検索結果 ------------------ */}
      {selected && data && (
        <div style={{ margin: isMobileScreen ? "0 5%" : "0 10%" }}>
          <h2
            style={{
              fontFamily: "Noto Sans JP",
              fontWeight: 600,
              fontSize: isMobileScreen
                ? "16pt"
                : isSmallScreen
                ? "18pt"
                : "22pt",
              textAlign: "center",
              margin: "10px 0",
            }}
          >
            {selected}番のシャーレ
          </h2>
          <div
            style={{
              border: `3px solid ${ACCENT}`,
              borderRadius: "20px",
              padding: isMobileScreen ? "14px" : "24px",
              margin: "16px 0",
              backgroundColor: "white",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: isMobileScreen ? "16px" : "24px",
                flexWrap: "wrap",
              }}
            >
              {data.days.map((d) => (
                <PhotoCell
                  key={d.key}
                  src={photos?.[d.key]}
                  dayLabel={d.label}
                  dateLabel={d.date}
                  published={d.published}
                  no={selected}
                  onZoom={(src, caption) => setZoom({ src, caption })}
                />
              ))}
            </div>
            <p
              style={{
                fontFamily: "Noto Sans JP",
                fontSize: isMobileScreen ? "10pt" : "12pt",
                color: "#444",
                margin: "16px 0 0 0",
              }}
            >
              写真の上のほうにあるのが、シャーレの「ふた」だよ。ふたには「なっとう」「こうじ」と番号が書いてあって、そのすぐ下にあるのが、その菌でかいた絵です。左右どちらがなっとうかは写真によってちがうので、ふたの字を見てたしかめてね。
            </p>
          </div>
          {data.days.some((d) => d.published && photos?.[d.key]) && (
            <p
              style={{
                fontFamily: "Noto Sans JP",
                fontSize: isMobileScreen ? "10pt" : "12pt",
                textAlign: "center",
                color: "#666",
              }}
            >
              写真をタップすると大きく見られるよ
            </p>
          )}
        </div>
      )}

      {selected && !data && <Paragraph text="写真を読みこんでいます…" />}

      {/* ------------------ 見くらべのヒント ------------------ */}
      <Subtitle text="くらべてみよう" />
      <Paragraph
        text={
          `・${dayNames}で、絵の大きさやこさはどうかわったかな？かいた線どおりに育ったかな？\\r\\n・納豆菌と麹菌、どちらが早く大きくなったかな？`
        }
      />

      <Subtitle text="納豆菌と麹菌は、ぜんぜんちがう生きもの" />
      <Paragraph
        text={
          "同じ「菌」という名前がついていても、納豆菌と麹菌は、じつはまったく別のグループの生きものです。\\r\\n納豆菌は「細菌（さいきん・バクテリア）」のなかま。たった1つの細胞でできていて、細胞の中に核（かく）がありません。ふえるときは、体をまっぷたつに分けて数をふやします。だからシャーレの上では、ぬるっとした、つやのあるかたまりが、じわじわとひろがっていきます。\\r\\n麹菌は「真菌（しんきん）」のなかま。つまりカビやキノコの仲間で、細胞の中にちゃんと核があります。じつは、真菌はわたしたち人間と同じ「真核生物」のグループで、細菌よりも人間に近い生きものなのです。麹菌は菌糸（きんし）という細い糸をどんどんのばして育つので、綿（わた）のようにふわふわした見た目になり、大きくなると胞子（ほうし）をつくって黄緑色に変わります。\\r\\nグループがちがう＝体のつくりも、ふえ方もちがう。だから、生えてきたようすもこんなにちがって見えるのです。2まいのシャーレを見くらべて、どこがどうちがうか、絵や言葉でメモしてみよう。"
        }
      />

      <Subtitle text="あれ？かいてないものが生えてる？" />
      <Paragraph
        text={
          "自分がかいた絵とは関係ないところに、色や形のちがうつぶつぶが出てくることがあります。オレンジ色、黒、赤、ピンク……。これは納豆菌でも麹菌でもなく、どこかからまぎれこんだ別の菌です。こういうのを「コンタミネーション（コンタミ）」といいます。\\r\\nでは、なぜ入りこんだのでしょう？\\r\\n・シャーレのふたを開けていた時間はどれくらいだったかな？\\r\\n・そのあいだ、空気中には何がとんでいたのかな？\\r\\n・手や、つくえや、筆はどうだったかな？\\r\\n・くしゃみやおしゃべりは、どうだろう？\\r\\nじつは、わたしたちのまわりの空気やものの表面には、目に見えない菌がたくさんいます。研究者が実験をするときにコンタミがおきると、実験の結果がわからなくなってしまうので、アルコールで消毒したり、菌の入らないきれいな台を使ったりして、必死にふせいでいます。\\r\\nみんなのシャーレにコンタミはいたかな？どうすればふせげたと思う？考えてみよう。"
        }
      />

      {/* ------------------ みんなのシャーレ ------------------ */}
      <Subtitle text="みんなのシャーレ" />
      <div style={{ margin: isMobileScreen ? "0 5% 20px" : "0 10% 20px" }}>
        <button
          onClick={() => setShowAll((v) => !v)}
          style={{
            fontFamily: "Noto Sans JP",
            fontWeight: 600,
            fontSize: isMobileScreen ? "11pt" : "14pt",
            color: showAll ? "white" : ACCENT,
            backgroundColor: showAll ? ACCENT : "white",
            border: `2px solid ${ACCENT}`,
            borderRadius: "12px",
            padding: "10px 24px",
            cursor: "pointer",
          }}
        >
          {showAll ? "番号のいちらんをとじる" : "番号のいちらんをひらく"}
        </button>

        {showAll && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(auto-fill, minmax(${
                isMobileScreen ? "52px" : "64px"
              }, 1fr))`,
              gap: "8px",
              marginTop: "16px",
            }}
          >
            {Array.from(
              { length: data?.participantCount ?? 79 },
              (_, i) => i + 1
            ).map((n) => (
              <button
                key={n}
                onClick={() => handleSearch(String(n))}
                style={{
                  fontFamily: "Noto Sans JP",
                  fontWeight: 600,
                  fontSize: isMobileScreen ? "10pt" : "12pt",
                  color: selected === n ? "white" : "#333",
                  backgroundColor: selected === n ? ACCENT : "#F5F5F5",
                  border: "1px solid #DDD",
                  borderRadius: "8px",
                  padding: isMobileScreen ? "8px 0" : "10px 0",
                  cursor: "pointer",
                }}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ------------------ 保護者・先生の方へ ------------------ */}
      <Subtitle text="保護者・先生の方へ" />
      <Paragraph
        text={
          "本ワークショップでは、納豆菌（枯草菌）と麹菌（Aspergillus oryzae）という、いずれも食品の製造に古くから用いられている微生物を使用しました。\\r\\n一方で、培地はお子さまに直接お持ち帰りいただいておりません。作業中に空気中や手指から意図しない微生物が混入することがあり、それらが増殖したのちに開封すると、アレルギーや感染の原因となる可能性を否定できないためです。培養したシャーレはすべてiGEM TSUKUBAが密封したまま管理し、大学の実験室で規定にしたがって観察・撮影を行ったのち、オートクレーブ（高圧蒸気滅菌）により滅菌して廃棄いたします。\\r\\nこのページでは日をおいて撮影した写真を掲載し、菌が育っていくようすをご覧いただけるようにしています。ご不明な点がありましたら、ページ上部の「お問い合わせ」よりご連絡ください。"
        }
      />

      {zoom && (
        <Lightbox
          src={zoom.src}
          caption={zoom.caption}
          onClose={() => setZoom(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default FungiWorld;
